import { MAP_ANIMATION_SETTINGS } from '/settings.js';
import { restoreActiveFlowFromUrl, setUIState, getUIState } from '../utils/clearAll.js';

// Shared overlay management
let sharedOverlay = null;
let overlayUsers = 0;
let overlayCallbacks = [];

function cleanupOverlayIfEmpty() {
    const overlay = sharedOverlay || document.querySelector('.popup-overlay');
    if (!overlay) return;

    const hasPopup = overlay.querySelector('.popup');
    const menuOpen = getUIState('menuOpen');

    if (!hasPopup && !menuOpen) {
        overlay.remove();
        if (overlay === sharedOverlay) {
            sharedOverlay = null;
            overlayUsers = 0;
            overlayCallbacks = [];
        }
    }
}

function createSharedOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'popup-overlay';
    overlay.addEventListener('mousedown', (e) => {
        if (e.target === overlay) {
            // Notify all registered listeners
            [...overlayCallbacks].forEach((cb) => {
                try { cb(); } catch (err) { console.error('Overlay callback failed:', err); }
            });
        }
    });
    document.body.appendChild(overlay);
    return overlay;
}

export function getSharedOverlay() {
    if (!sharedOverlay || !document.body.contains(sharedOverlay)) {
        sharedOverlay = createSharedOverlay();
        overlayUsers = 0;
    }
    return sharedOverlay;
}

export function acquireOverlay() {
    const overlay = getSharedOverlay();
    overlayUsers += 1;
    overlay.classList.remove('fading-out');
    overlay.style.display = 'flex';
    return overlay;
}

export function releaseOverlay() {
    overlayUsers = Math.max(overlayUsers - 1, 0);
    if (sharedOverlay) {
        const hasPopup = sharedOverlay.querySelector('.popup');
        const menuOpen = getUIState('menuOpen');
        if (overlayUsers === 0 || (!hasPopup && !menuOpen)) {
            sharedOverlay.remove();
            sharedOverlay = null;
            overlayCallbacks = [];
            overlayUsers = 0;
        }
    }
    cleanupOverlayIfEmpty();
}

export function addOverlayListener(cb) {
    if (typeof cb === 'function') overlayCallbacks.push(cb);
}

export function removeOverlayListener(cb) {
    overlayCallbacks = overlayCallbacks.filter((fn) => fn !== cb);
}

// Force close all popups and reset the overlay system
// Used by clearAllUI to ensure clean state before opening new popups
export function forceCloseAllPopups() {
    // Remove all popup elements
    document.querySelectorAll('.popup-overlay .popup').forEach(popup => popup.remove());

    // Remove the overlay itself
    if (sharedOverlay) {
        sharedOverlay.remove();
        sharedOverlay = null;
    }

    // Reset state
    overlayUsers = 0;
    overlayCallbacks = [];

    // Final safety cleanup
    cleanupOverlayIfEmpty();
}

export default function Popup({ title = '', content = '', footer = '', onClose, size = 'medium' }) {
    // Use shared overlay
    const overlay = acquireOverlay();
    setUIState('popupOpen', true);

    // Keyboard-aware positioning (mobile): move popup up only when keyboard is visible
    const baseInnerHeight = window.innerHeight;
    const baseViewportHeight = window.visualViewport?.height || window.innerHeight;
    const keyboardThresholdPx = 140; // heuristic for mobile keyboard
    const keyboardThresholdRatio = 0.85;

    const updateKeyboardState = () => {
        const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
        const viewportDiff = Math.max(0, window.innerHeight - currentViewportHeight);
        const hasTextFocus = document.activeElement && (
            document.activeElement.tagName === 'INPUT' ||
            document.activeElement.tagName === 'TEXTAREA' ||
            document.activeElement.isContentEditable
        );
        const isKeyboardVisible = hasTextFocus && (
            viewportDiff > keyboardThresholdPx || currentViewportHeight < baseViewportHeight * keyboardThresholdRatio
        );
        if (isKeyboardVisible) {
            overlay.classList.add('keyboard-visible');
        } else {
            overlay.classList.remove('keyboard-visible');
        }
    };

    // Create popup container
    const popup = document.createElement('div');
    popup.className = `popup popup-${size}`;

    // Create header
    const header = document.createElement('div');
    header.className = 'popup-header';
    // Title container
    const titleContainer = document.createElement('div');
    titleContainer.className = 'popup-title';
    if (typeof title === 'string') {
        titleContainer.textContent = title;
    } else if (title instanceof Node) {
        titleContainer.appendChild(title);
    }
    header.appendChild(titleContainer);
    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'popup-close';
    closeBtn.type = 'button';
    closeBtn.title = 'Close';
    closeBtn.innerHTML = '&times;';
    closeBtn.onclick = () => closeImmediately(); // Wrap to use current handler
    header.appendChild(closeBtn);
    popup.appendChild(header);

    // Main content area
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'popup-content';
    if (typeof content === 'string') {
        contentWrapper.innerHTML = content;
    } else if (content instanceof Node) {
        contentWrapper.appendChild(content);
    }
    popup.appendChild(contentWrapper);

    // Footer area
    const footerWrapper = document.createElement('div');
    footerWrapper.className = 'popup-footer';
    if (typeof footer === 'string') {
        footerWrapper.innerHTML = footer;
    } else if (footer instanceof Node) {
        footerWrapper.appendChild(footer);
    }
    popup.appendChild(footerWrapper);

    // Add popup to overlay
    overlay.appendChild(popup);
    document.body.appendChild(overlay);

    // Close when clicking outside the popup (on the overlay)
    const onOverlayClick = () => closeImmediately();
    addOverlayListener(onOverlayClick);

    // Listen for viewport changes (keyboard show/hide)
    window.visualViewport?.addEventListener('resize', updateKeyboardState);
    window.visualViewport?.addEventListener('scroll', updateKeyboardState);
    window.addEventListener('resize', updateKeyboardState);
    updateKeyboardState();

    // Store current onClose handler
    let currentOnClose = onClose;

    function closeImmediately() {
        popup.remove();
        removeOverlayListener(onOverlayClick);
        window.visualViewport?.removeEventListener('resize', updateKeyboardState);
        window.visualViewport?.removeEventListener('scroll', updateKeyboardState);
        window.removeEventListener('resize', updateKeyboardState);
        overlay.classList.remove('keyboard-visible');
        releaseOverlay();
        setUIState('popupOpen', false);
        if (typeof currentOnClose === 'function') currentOnClose();
        // Restore header button state after any popup is closed
        restoreActiveFlowFromUrl();

        // Safety: ensure overlay is removed if no popups remain
        cleanupOverlayIfEmpty();
    }

    function closeWithAnimation() {
        return new Promise((resolve) => {
            const duration = MAP_ANIMATION_SETTINGS.FADE_DURATION_MS || 300;

            // Add fade-out classes for smooth CSS animations
            overlay.classList.add('fading-out');
            popup.classList.add('fading-out');

            setTimeout(() => {
                popup.remove();
                removeOverlayListener(onOverlayClick);
                window.visualViewport?.removeEventListener('resize', updateKeyboardState);
                window.visualViewport?.removeEventListener('scroll', updateKeyboardState);
                window.removeEventListener('resize', updateKeyboardState);
                overlay.classList.remove('keyboard-visible');
                releaseOverlay();
                setUIState('popupOpen', false);
                if (typeof currentOnClose === 'function') currentOnClose();
                // Restore header button state after any popup is closed
                restoreActiveFlowFromUrl();
                // Safety: ensure overlay is removed if no popups remain
                cleanupOverlayIfEmpty();
                resolve();
            }, duration);
        });
    }

    // Return an object with methods to control the popup
    return {
        element: overlay, // Expose DOM element
        close: closeImmediately, // IMMEDIATE CLOSING - NO DELAY!
        closeWithAnimation: closeWithAnimation, // ANIMATED CLOSING - WITH DELAY
        fadeOut: closeWithAnimation, // Alias for better naming
        updateOnClose: (newHandler) => {
            currentOnClose = newHandler;
        },
        updateContent: ({ title, content, footer }) => {
            // Update title
            if (title !== undefined) {
                titleContainer.innerHTML = '';
                if (typeof title === 'string') {
                    titleContainer.textContent = title;
                } else if (title instanceof Node) {
                    titleContainer.appendChild(title);
                }
            }
            // Update content
            if (content !== undefined) {
                contentWrapper.innerHTML = '';
                if (typeof content === 'string') {
                    contentWrapper.innerHTML = content;
                } else if (content instanceof Node) {
                    contentWrapper.appendChild(content);
                }
            }
            // Update footer
            if (footer !== undefined) {
                footerWrapper.innerHTML = '';
                if (typeof footer === 'string') {
                    footerWrapper.innerHTML = footer;
                } else if (footer instanceof Node) {
                    footerWrapper.appendChild(footer);
                }
            }
        }
    };
} 
